/** biome-ignore-all lint/suspicious/noExplicitAny: penjelasannya */

import {
	Badge,
	Box,
	Card,
	Center,
	Grid,
	Group,
	Loader,
	Progress,
	Stack,
	Text,
	ThemeIcon,
	Title,
} from "@mantine/core";
import {
	AlertTriangle,
	CheckCircle,
	Clock,
	MessageCircle,
	TrendingUp,
} from "lucide-react";
import type React from "react";
import {
	Bar,
	BarChart,
	CartesianGrid,
	LabelList,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { useSnapshot } from "valtio";
import { useApiQuery } from "@/hooks/useApiQuery";
import { useIsDark } from "@/hooks/useIsDark";
import { useTranslate } from "@/hooks/useTranslate";
import { i18nStore } from "@/store/i18n";
import { CHART } from "@/theme";

// ─── Types ────────────────────────────────────────────────────────────────────

type KpiItem = {
	id: number;
	target: string;
	title: string;
	value: string;
	subtitle: string;
	trend?: "positive";
	icon: React.ElementType;
};

type JennaAnalyticsData = {
	stats: {
		interaksiHariIni: number;
		changeFromYesterday: number;
		jawabanOtomatis: number;
		jawabanOtomatisCount: number;
		waktuRespon: string;
		belumDitindak: number;
	};
	chartMingguan: Array<{ day: string; count: number }>;
	topTopics: Array<{ topic: string; count: number }>;
	jamTersibuk: Array<{ slot: string; pct: number }>;
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

async function fetchJennaAnalytics(): Promise<JennaAnalyticsData> {
	// Fetch via server proxy — avoids CORS & doesn't require VITE_* env vars at build time
	const r = await fetch("/api/jenna/analytics");
	if (!r.ok) throw new Error(`HTTP ${r.status}`);
	return (await r.json()) as JennaAnalyticsData;
}

function useJennaAnalytics() {
	const query = useApiQuery(["jenna", "analytics"], fetchJennaAnalytics, {
		autoRefresh: true,
	});

	return {
		data: query.data ?? null,
		loading: query.isLoading,
		error: query.error ? query.error.message : null,
	};
}

// ─── Component ────────────────────────────────────────────────────────────────

const JennaAnalytic = () => {
	const t = useTranslate();
	const dark = useIsDark();
	const { tampilkanGrid } = useSnapshot(i18nStore);

	const { data, loading, error } = useJennaAnalytics();

	if (loading) {
		return (
			<Center h={400}>
				<Loader size="md" />
			</Center>
		);
	}

	if (error) {
		return (
			<Center h={400}>
				<Stack align="center" gap="sm">
					<AlertTriangle size={32} color={CHART.red} />
					<Text c="red" fw={500}>
						{error}
					</Text>
				</Stack>
			</Center>
		);
	}

	const change = data?.stats.changeFromYesterday ?? 0;

	const kpiData = [
		{
			id: 1,
			target: "jenna.kpi-interaksi",
			title: t.jennaAnalytic.interaksiHariIni,
			value: data?.stats.interaksiHariIni.toString() ?? "—",
			subtitle: `${change >= 0 ? "+" : ""}${change}% ${t.jennaAnalytic.plusDariKemarin}`,
			trend: change >= 0 ? "positive" : undefined,
			icon: MessageCircle,
		},
		{
			id: 2,
			target: "jenna.kpi-otomatis",
			title: t.jennaAnalytic.jawabanOtomatis,
			value: data ? `${data.stats.jawabanOtomatis}%` : "—",
			subtitle: `dari ${data?.stats.interaksiHariIni ?? 0} interaksi`,
			icon: CheckCircle,
		},
		{
			id: 3,
			target: "jenna.kpi-belum-ditindak",
			title: t.jennaAnalytic.belumDitindak,
			value: data?.stats.belumDitindak.toString() ?? "—",
			subtitle: t.jennaAnalytic.perluResponManual,
			icon: AlertTriangle,
		},
		{
			id: 4,
			target: "jenna.kpi-waktu-respon",
			title: t.jennaAnalytic.waktuRespon,
			value: data?.stats.waktuRespon ?? "—",
			subtitle: t.jennaAnalytic.rataRata,
			icon: Clock,
		},
	] satisfies KpiItem[];

	const chartData = (data?.chartMingguan ?? []).map((item) => ({
		day: item.day,
		total: item.count,
	}));

	const topTopics = data?.topTopics ?? [];

	const busyHours = (data?.jamTersibuk ?? []).map((item) => ({
		period: item.slot,
		percentage: item.pct,
	}));

	return (
		<Stack gap="lg">
			{/* KPI Cards */}
			<Grid gutter="md">
				{kpiData.map((item) => (
					<Grid.Col key={item.id} span={{ base: 12, sm: 6, lg: 3 }}>
						<Card
							data-ai-target={item.target}
							p="md"
							radius="xl"
							withBorder
							style={{
								backgroundColor: "var(--app-card)",
								borderColor: "var(--app-border)",
								boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
								transition: "transform 0.15s ease, box-shadow 0.15s ease",
							}}
							h="100%"
						>
							<Group justify="space-between" align="flex-start" w="100%">
								<Stack gap={2}>
									<Text size="sm" c="dimmed">
										{item.title}
									</Text>
									<Text size="xl" fw={700} c={dark ? "white" : "gray.9"}>
										{item.value}
									</Text>
									<Group gap={4} align="flex-start">
										{item.trend === "positive" && (
											<TrendingUp size={14} color={CHART.green} />
										)}
										<Text
											size="xs"
											c={
												item.trend === "positive"
													? "green"
													: dark
														? "gray.4"
														: "gray.5"
											}
										>
											{item.subtitle}
										</Text>
									</Group>
								</Stack>
								<ThemeIcon
									color="darmasaba-navy.7"
									variant="filled"
									size="lg"
									radius="xl"
								>
									<item.icon style={{ width: "60%", height: "60%" }} />
								</ThemeIcon>
							</Group>
						</Card>
					</Grid.Col>
				))}
			</Grid>

			{/* Chart - Interaksi Chatbot */}
			<Card
				data-ai-target="jenna.grafik-interaksi"
				p="md"
				radius="xl"
				withBorder
				style={{
					backgroundColor: "var(--app-card)",
					borderColor: "var(--app-border)",
					boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
				}}
			>
				<Group justify="space-between" mb="md">
					<Title order={4} c={dark ? "white" : "gray.9"}>
						{t.jennaAnalytic.interaksiChatbot}
					</Title>
				</Group>
				<ResponsiveContainer width="100%" height={300}>
					<BarChart data={chartData}>
						{tampilkanGrid && (
							<CartesianGrid
								strokeDasharray="3 3"
								vertical={false}
								stroke="var(--app-border)"
							/>
						)}
						<XAxis
							dataKey="day"
							axisLine={false}
							tickLine={false}
							tick={{ fill: "var(--app-text)" }}
						/>
						<YAxis
							axisLine={false}
							tickLine={false}
							tick={{ fill: "var(--app-text)" }}
						/>
						<Tooltip
							contentStyle={{
								backgroundColor: "var(--app-card)",
								borderColor: "var(--app-border)",
								borderRadius: "8px",
							}}
							itemStyle={{ color: "var(--app-text)" }}
							labelStyle={{ color: "var(--app-text)" }}
							cursor={{ fill: "var(--app-track)" }}
						/>
						<Bar
							dataKey="total"
							fill="#396aaaff"
							radius={[8, 8, 0, 0]}
							maxBarSize={60}
						>
							<LabelList
								dataKey="total"
								position="top"
								style={{ fill: "var(--app-text)", fontSize: 11 }}
							/>
						</Bar>
					</BarChart>
				</ResponsiveContainer>
			</Card>

			{/* Bottom - Topik & Jam Tersibuk */}
			<Grid gutter="lg">
				<Grid.Col span={{ base: 12, lg: 6 }}>
					<Card
						data-ai-target="jenna.topik-pertanyaan"
						p="md"
						radius="xl"
						withBorder
						bg={dark ? "#1E293B" : "white"}
						style={{
							borderColor: dark ? "#334155" : "white",
							boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
						}}
						h="100%"
					>
						<Title order={4} c={dark ? "white" : "gray.9"} mb="md">
							{t.jennaAnalytic.topikPertanyaan}
						</Title>
						<Stack gap="xs">
							{topTopics.map((item) => (
								<Box
									key={item.topic}
									p="sm"
									bg="var(--app-card-alt)"
									style={{
										transition: "background-color 0.15s ease",
										cursor: "pointer",
									}}
								>
									<Group justify="space-between">
										<Text size="sm" fw={500} c={dark ? "white" : "gray.9"}>
											{item.topic}
										</Text>
										<Badge
											variant="light"
											color="darmasaba-blue"
											radius="sm"
											fw={600}
										>
											{item.count}x
										</Badge>
									</Group>
								</Box>
							))}
						</Stack>
					</Card>
				</Grid.Col>

				<Grid.Col span={{ base: 12, lg: 6 }}>
					<Card
						data-ai-target="jenna.jam-tersibuk"
						p="md"
						radius="xl"
						withBorder
						bg={dark ? "#1E293B" : "white"}
						style={{
							borderColor: dark ? "#334155" : "white",
							boxShadow: "0 1px 3px 0 rgb(0 0 0 / 0.1)",
						}}
						h="100%"
					>
						<Title order={4} c={dark ? "white" : "gray.9"} mb="md">
							{t.jennaAnalytic.jamTersibuk}
						</Title>
						<Stack gap="md">
							{busyHours.map((item) => (
								<Box key={item.period}>
									<Group justify="space-between" mb={5}>
										<Text size="sm" fw={500} c={dark ? "white" : "gray.9"}>
											{item.period}
										</Text>
										<Text size="sm" fw={600} c={dark ? "white" : "gray.9"}>
											{item.percentage}%
										</Text>
									</Group>
									<Progress
										value={item.percentage}
										size="lg"
										radius="xl"
										color="darmasaba-navy.7"
										animated
									/>
								</Box>
							))}
						</Stack>
					</Card>
				</Grid.Col>
			</Grid>
		</Stack>
	);
};

export default JennaAnalytic;
